import { useClientStore } from "@/store/client";
import { useDatalayerStore } from "@/store/datalayer";
import { useMagasinStore } from "@/store/magasin";
import HomepageCodes from "@/utils/home-codes";
import apiDriveLad from "@/services/http/api-drive/magasin/lad";
import communesApiDrive from "@/services/http/api-drive/communes";
import merch from "@/services/http/api-drive/magasin/merch";
import cmsApiDriveService from "@/services/http/api-drive/cms";
import { useProductDetailsTracking } from "~/mixins/tracking/product-details"
import { pushProps, usePush } from "@/components/pages/mixins/push-mixin"

export const pageHomeProps = () => ({
  serviceLadId: {
    type: Number,
    required: true
  },
  prospectus: {
    type: Array,
    required: false,
    default: () => []
  },
  isNational: {
    type: Boolean,
    required: true
  },
  showAboLad: {
    type: Boolean,
    required: false,
    default: false
  },
  magasinId: {
    type: Number,
    required: true
  },
  shopName: {
    type: String,
    required: true
  },
  webAlias: {
    type: String,
    required: true
  },
  shopfirstSlotAvailable: {
    type: String,
    required: false,
    default: null
  },
  shopLastSlotAvailable: {
    type: String,
    required: false,
    default: null
  },
  prospectId: {
    type: String,
    required: false,
    default: ''
  },
  userId: {
    type: String,
    required: false,
    default: ''
  },
  modesCommande: {
    type: Array,
    required: false
  },
  isFlocklerLoaded: {
    type: Boolean,
    required: false,
    default: false
  },
  magasin: {
    type: Object,
    required: false,
    default: () => {}
  },
  ...pushProps()
})

export const pageHomeEmits = () => ([
  "communeValidated"
])

export const usePageHome = (props, context, deliveryTermsPopinRef) => {

  const { trackProductPage } = useProductDetailsTracking();

  const { getPushProductsFromTransformed, getPushListFromTransformed, getPushTitleFromTransformed, getPushDataFromTransformed } = usePush(props.pushZones);

  const clientStore = useClientStore();
  const magasinStore = useMagasinStore();
  const datalayerStore = useDatalayerStore();
  const app = useNuxtApp();

  const showShippingPopin = ref(false)
  const showCountriesListPopin = ref(false)
  const showPlanningPopin = ref(false)
  const showTarifLadPopin = ref(false)
  const showCCPopin = ref(false)
  const showLCPopin = ref(false)
  const deliveryMode = ref('DRIVE')
  const communesLad = ref([])
  const communes = ref(null)
  const noCommunes = ref(true)
  const isEligible = ref(false)
  const commune = ref(null)
  const infoMessages = ref("")
  const alertMessages = ref("")
  const legalContent = ref({})

  const { abonnementExpireInMagasin, isPro } = storeToRefs(clientStore);

  const { setLocation, setStoreLocatorContext } = datalayerStore;
  const { displayOrCloseStoreLocatorPopin } = magasinStore;

  const prospectusShow = computed(() => {
    return props.prospectus && props.prospectus.length !== 0;
  })
  const pushListSlider = computed(() => {
    return getPushListFromTransformed(isPro.value ? HomepageCodes.PRO_HP_SLIDER : HomepageCodes.HP_SLIDER);
  })
  const pushHpProduits = computed(() => {
    return getPushProductsFromTransformed(isPro.value ? HomepageCodes.PRO_HP_PRODUITS : HomepageCodes.HP_PRODUITS, 1);
  })
  const showRecettes = computed(() => {
    return useRuntimeConfig().public.MIAM_SHOW_RECETTES && isPro.value;
  })
  const recettePosition = computed(() => {
    return useRuntimeConfig().public.MIAM_HOME_PAGE_POSITION;
  })
  const modesCommandeByType = computed(() => {
    const modesCommandeByType = {}
    if (props.modesCommande) {
      props.modesCommande.forEach((elem) => { modesCommandeByType[elem.type] = elem })
    }
    return modesCommandeByType;
  })
  const classesFlockler = computed(() => {
    if (props.isFlocklerLoaded || !props.isNational) {
      return ['u-bgcolor-bg u-pb-xlg'];
    }
    return ['u-bgcolor-bg u-pb-lg'];
  })
  const abonnementExpireInCurrentMagasin = computed(() => {
    return abonnementExpireInMagasin.value(props.magasinId)
  })
  const isEasyBox = computed(() => {
    return modesCommandeByType.value.PRC && modesCommandeByType.value.PRC.type_picto.code === "EASY_BOX"
  })

  const contactsCorapro = computed(() => {
    return props.magasin.attributes && 
    props.magasin.attributes.infos_corapro && 
    props.magasin.attributes.infos_corapro[0] ? props.magasin.attributes.infos_corapro[0] : null
  })

  const onProductIntersect = (index, pushName, position, productNumber) => {
    if (index % 6 === 0) {
      const list = getPushProductsFromTransformed(pushName, position, productNumber)
      const products = list && list.length > 0 ? list.slice(index - 6, index) : []
      trackProductPage(
        {
          type: trackingCodes.IMPRESSIONDONE,
          products,
          index,
          actionField: "HP: push produit"
        })
    }
  }
  const toggleCountriesListPopin = () => {
    showCountriesListPopin.value = !showCountriesListPopin.value;
  }
  const closeCountriesListPopin = () => {
    showCountriesListPopin.value = false;
  }
  const closeCCPopin = () => {
    showCCPopin.value = false;
  }
  const closeLCPopin = () => {
    showLCPopin.value = false;
  }
  const closeTarifLadPopin = () => {
    showTarifLadPopin.value = false;
  }
  const toggleShippingPopin = () => {
    showShippingPopin.value = !showShippingPopin.value;
  }
  const togglePlanningPopin = (type) => {
    deliveryMode.value = type;
    showPlanningPopin.value = !showPlanningPopin.value;
  }
  const closePlanningPopin = () => {
    showPlanningPopin.value = false;
    if (commune.value) {
      resetCommune()
    }
  }
  const getCommunes = async () => {
    try {
      communesLad.value = await app.$apiDrive
        .use(apiDriveLad)
        .communesLad(props.magasinId);
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(app.$apiDrive.getErrorData(err));
    }
  }
  const fetchCommunes = async (codePostal) => {
    try {
      noCommunes.value = false;
      communes.value = await app.$apiDrive.use(communesApiDrive).communes({
        code_postal: codePostal
      });
      if (communes.value.length === 0) {
        noCommunes.value = true;
      }
    } catch (err) {
      noCommunes.value = true;
    }
  }
  const communeValidated = async (c) => {
    try {
      const communesMagasin = await app.$apiDrive.use(apiDriveLad).communesLad(props.magasinId);
      const exist = communesMagasin.find((communeMagasin) => {
        return (
          communeMagasin.attributes.code_postal === c.code_postal &&
          communeMagasin.attributes.code_insee === c.code_insee
        );
      });

      if (!exist) {
        isEligible.value = true;
      } else {
        context.emit("communeValidated", c);
      }
    } catch (err) {
      isEligible.value = true;
    }
  }
  const resetCommunes = () => {
    communes.value = [];
    isEligible.value = false;
  }
  const communeSelected = (c) => {
    commune.value = c;
  }
  const resetCommune = () => {
    commune.value = null;
  }
  const getInfoMessages = async () => {
    try {
      const messages = await app.$apiDrive.use(merch).infosDrive(props.magasinId);
      const info = messages.find(e => (e.type === "message_info" && e.info.message !== ""))
      infoMessages.value = info ? info.info.message : ""
      const alert = messages.find(e => (e.type === "message_alerte" && e.info.message !== ""))
      alertMessages.value = alert ? alert.info.message : ""
    } catch (err) {
    }
  }
  const getDeliveryTermsContent = async () => {
    try {
      legalContent.value = await app.$apiDrive.use(cmsApiDriveService).fetchContent(`/legal/informations-juridiques/infos-specifiques`, { magasinId: props.magasinId });
      showDeliveryTermsPopin();
    } catch (e) {
    }
  }
  const showDeliveryTermsPopin = () => {
    deliveryTermsPopinRef.value.show();
  }
  const openStoreLocator = () => {
    setStoreLocatorContext("home");
    displayOrCloseStoreLocatorPopin();
  }
  const callToActionModeCommandeOverview = (type) => {
    switch (type) {
      case "CC":
        showCCPopin.value = true;
        break;
      case "PRP":
        openStoreLocator()
        break;
      case "LAD":
        toggleShippingPopin()
        break;
      case "CEV":
        togglePlanningPopin('CEV')
        break;
      case "DRIVE":
        togglePlanningPopin('DRIVE')
        break;
      case "PRC":
        togglePlanningPopin('PRC')
        break;
      case "PRC_DELIPOP":
        togglePlanningPopin('PRC_DELIPOP')
        break;
      case "LC":
        showLCPopin.value = true;
        break;
    }
  }
  const actionSecondaireModeCommandeOverview = (type) => {
    switch (type) {
      case "CEV":
        navigateToInternal("/infos-et-services/les-magasins/profitez-des-services/drive-pieton")
        break;
      case "LAD":
        showTarifLadPopin.value = true;
        break;
      case "PRC":
        navigateToInternal("/infos-et-services/les-magasins-cora/profitez-des-services/easybox")
        break;
      case "PRC_DELIPOP":
        navigateTo("/infos-et-services/les-magasins-cora/profitez-des-services/delipop")
        break;
    }
  }

  const mounted = () => {
    getInfoMessages();
    setLocation("Homepage")
  }

  return {
    showShippingPopin,
    showCountriesListPopin,
    showPlanningPopin,
    showTarifLadPopin,
    showCCPopin,
    showLCPopin,
    deliveryMode,
    communesLad,
    communes,
    noCommunes,
    isEligible,
    commune,
    infoMessages,
    alertMessages,
    legalContent,
    abonnementExpireInMagasin, isPro,
    setLocation, setStoreLocatorContext,
    displayOrCloseStoreLocatorPopin,
    prospectusShow,
    pushListSlider,
    pushHpProduits,
    showRecettes,
    recettePosition,
    modesCommandeByType,
    classesFlockler,
    abonnementExpireInCurrentMagasin,
    isEasyBox,
    contactsCorapro,
    onProductIntersect,
    toggleCountriesListPopin,
    closeCountriesListPopin,
    closeCCPopin,
    closeLCPopin,
    closeTarifLadPopin,
    toggleShippingPopin,
    togglePlanningPopin,
    closePlanningPopin,
    getCommunes,
    fetchCommunes,
    communeValidated,
    resetCommunes,
    communeSelected,
    resetCommune,
    getInfoMessages,
    getDeliveryTermsContent,
    showDeliveryTermsPopin,
    openStoreLocator,
    callToActionModeCommandeOverview,
    actionSecondaireModeCommandeOverview,
    getPushProductsFromTransformed,
    getPushListFromTransformed,
    getPushTitleFromTransformed,
    getPushDataFromTransformed,
    mounted,
    HomepageCodes
  }

}